import type { ApiError } from '../api/client';
import type { DeliveryMode, ZoneGeometry, ZoneInput, ZoneView } from '../api/endpoints';
import { clockMinutes, isClockTime, normaliseClock } from './clock';
import { vertexCount } from './geometry';

/**
 * Форма зоны (FR-502, FR-503, A12): состояние, проверка и тело запроса.
 *
 * SLA проверяется здесь только как целое положительное число. Допустимый диапазон —
 * параметр реестра П-И (`sla_minutes`), и проверять его сервер обязан той же функцией,
 * что значения профиля (FR-417.1); дублировать границы на клиенте — значит однажды
 * разойтись с реестром.
 */

export const ZONE_NAME_MAX = 200;

export interface FactorRow {
  readonly from_time: string;
  readonly to_time: string;
  /** Строкой, как в поле: `1,35` и `1.35` оба допустимы. */
  readonly factor: string;
}

export interface ZoneFormState {
  readonly name: string;
  readonly mode: DeliveryMode;
  readonly priority: string;
  readonly sla_minutes: string;
  readonly traffic_factors: readonly FactorRow[];
  readonly geometry: ZoneGeometry | null;
  readonly is_active: boolean;
  readonly location_ids: readonly string[];
}

export type ZoneField =
  'name' | 'mode' | 'priority' | 'sla_minutes' | 'traffic_factors' | 'geometry' | 'location_ids';

/** Код ошибки поля — ключ в `messages.cabinet.zoneForm.errors`. */
export type ZoneFieldError =
  | 'nameRequired'
  | 'nameTooLong'
  | 'priorityInvalid'
  | 'slaInvalid'
  | 'slaOutOfRange'
  | 'factorsInvalid'
  | 'factorsOverlap'
  | 'geometryRequired'
  | 'geometryInvalid'
  | 'geometryRingTooShort'
  | 'geometryOutOfRange'
  | 'geometryEmpty'
  | 'modeInUse'
  | 'locationsUnknown';

export type ZoneFormErrors = Partial<Record<ZoneField, ZoneFieldError>>;

export function emptyZoneForm(departments: readonly DeliveryMode[]): ZoneFormState {
  return {
    name: '',
    mode: departments[0] ?? 'express',
    priority: '',
    sla_minutes: '',
    traffic_factors: [],
    geometry: null,
    is_active: true,
    location_ids: [],
  };
}

export function zoneFormFrom(zone: ZoneView): ZoneFormState {
  return {
    name: zone.name,
    mode: zone.mode === 'distribution' ? 'distribution' : 'express',
    priority: zone.priority === undefined ? '' : String(zone.priority),
    sla_minutes:
      zone.sla_minutes === null || zone.sla_minutes === undefined ? '' : String(zone.sla_minutes),
    traffic_factors: (zone.traffic_factors ?? []).map((row) => ({
      from_time: normaliseClock(row.from_time),
      to_time: normaliseClock(row.to_time),
      factor: String(row.factor),
    })),
    geometry: zone.geometry ?? null,
    is_active: zone.is_active ?? true,
    location_ids: [...(zone.location_ids ?? [])],
  };
}

export function emptyFactorRow(): FactorRow {
  return { from_time: '', to_time: '', factor: '' };
}

/** Целое число из поля; дробь, пробелы внутри, `1e3` — не число для приоритета. */
export function parseInteger(value: string): number | null {
  const trimmed = value.trim();

  return /^-?\d+$/.test(trimmed) ? Number(trimmed) : null;
}

export function parseFactor(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');

  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);

  return parsed > 0 ? parsed : null;
}

export function validateZoneForm(form: ZoneFormState): ZoneFormErrors {
  const errors: ZoneFormErrors = {};
  const name = form.name.trim();

  if (name === '') {
    errors.name = 'nameRequired';
  } else if (name.length > ZONE_NAME_MAX) {
    errors.name = 'nameTooLong';
  }

  // FR-503: приоритет обязателен и без умолчания — иначе выбор зоны на пересечении
  // недетерминирован.
  if (parseInteger(form.priority) === null) {
    errors.priority = 'priorityInvalid';
  }

  if (form.sla_minutes.trim() !== '') {
    const sla = parseInteger(form.sla_minutes);

    if (sla === null || sla <= 0) {
      errors.sla_minutes = 'slaInvalid';
    }
  }

  const factorError = validateFactors(form.traffic_factors);

  if (factorError !== null) {
    errors.traffic_factors = factorError;
  }

  // Зона без полигона ничего не покрывает: адрес в неё не попадёт никогда.
  if (form.geometry === null) {
    errors.geometry = 'geometryRequired';
  } else if (vertexCount(form.geometry) === 0) {
    errors.geometry = 'geometryInvalid';
  }

  return errors;
}

/**
 * Строки коэффициентов: время `HH:MM`, начало ≠ концу, коэффициент > 0, интервалы не
 * пересекаются. Пересечение не отклонить — значит отдать выбор коэффициента порядку
 * строк в JSON. Интервал через полночь (`22:00–06:00`) допустим и проверяется как два.
 */
function validateFactors(rows: readonly FactorRow[]): ZoneFieldError | null {
  const spans: [number, number][] = [];

  for (const row of rows) {
    const from = clockMinutes(row.from_time);
    const to = clockMinutes(row.to_time);

    if (
      !isClockTime(row.from_time) ||
      !isClockTime(row.to_time) ||
      from === null ||
      to === null ||
      from === to ||
      parseFactor(row.factor) === null
    ) {
      return 'factorsInvalid';
    }

    if (from < to) {
      spans.push([from, to]);
    } else {
      spans.push([from, MINUTES_IN_DAY], [0, to]);
    }
  }

  const sorted = [...spans].sort((left, right) => left[0] - right[0]);

  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1];
    const current = sorted[index];

    if (previous !== undefined && current !== undefined && current[0] < previous[1]) {
      return 'factorsOverlap';
    }
  }

  return null;
}

const MINUTES_IN_DAY = 1440;

export function hasZoneErrors(errors: ZoneFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Тело `POST /web/zones` (`ZoneInput`) и `PATCH /web/zones/{id}` (`ZonePatch`).
 *
 * Пустой SLA уходит `null` — «применяется значение профиля», а не ноль; пустой список
 * коэффициентов снимает их. `location_ids` заменяет точки зоны целиком — и при
 * создании, и при правке (ADR-0032).
 */
export function toZoneInput(form: ZoneFormState): ZoneInput {
  const sla = parseInteger(form.sla_minutes);

  return {
    name: form.name.trim(),
    priority: parseInteger(form.priority) ?? 0,
    mode: form.mode,
    sla_minutes: form.sla_minutes.trim() === '' ? null : sla,
    traffic_factors: form.traffic_factors.map((row) => ({
      from_time: row.from_time.trim(),
      to_time: row.to_time.trim(),
      factor: parseFactor(row.factor) ?? 1,
    })),
    ...(form.geometry === null ? {} : { geometry: form.geometry }),
    is_active: form.is_active,
    location_ids: [...form.location_ids],
  };
}

const SERVER_FIELDS: Readonly<Record<string, ZoneField>> = {
  name: 'name',
  priority: 'priority',
  sla_minutes: 'sla_minutes',
  traffic_factors: 'traffic_factors',
  geometry: 'geometry',
  location_ids: 'location_ids',
};

/**
 * Коды проверки полигона сервером (`ST_IsValid`, кольца, диапазон WGS 84) → подпись у
 * поля границы. Незамкнутое кольцо клиент сам не отправляет (`contourToRing` замыкает),
 * поэтому `ring_not_closed` читается как общее «граница некорректна».
 */
const GEOMETRY_CODES: Readonly<Record<string, ZoneFieldError>> = {
  ring_too_short: 'geometryRingTooShort',
  position_out_of_range: 'geometryOutOfRange',
  position_invalid: 'geometryOutOfRange',
  polygon_empty: 'geometryEmpty',
  ring_not_closed: 'geometryInvalid',
  geometry_invalid: 'geometryInvalid',
};

/**
 * Отказ сервера → ошибки по полям. SLA вне диапазона реестра сервер называет кодом
 * проверки параметра (`out_of_range`, `below_min`, …) — для человека это одно:
 * «вне допустимого диапазона».
 */
export function zoneErrorsFromProblem(error: ApiError): {
  readonly fields: ZoneFormErrors;
  readonly general: boolean;
} {
  // Отдел зоны не меняется, пока в ней открыты смены: курьеры остались бы в зоне
  // чужого отдела посреди смены.
  if (error.code === 'zone_in_use') {
    return { fields: { mode: 'modeInUse' }, general: false };
  }

  const fields: ZoneFormErrors = {};

  for (const item of error.problem.errors ?? []) {
    const field = SERVER_FIELDS[item.field.split('.')[0] ?? ''];

    if (field === undefined) {
      continue;
    }

    if (field === 'geometry') {
      fields.geometry = GEOMETRY_CODES[item.code] ?? 'geometryInvalid';
      continue;
    }

    if (field === 'location_ids') {
      fields.location_ids = 'locationsUnknown';
      continue;
    }

    fields[field] =
      field === 'sla_minutes'
        ? 'slaOutOfRange'
        : field === 'name'
          ? 'nameRequired'
          : field === 'priority'
            ? 'priorityInvalid'
            : field === 'traffic_factors'
              ? 'factorsInvalid'
              : 'geometryInvalid';
  }

  return { fields, general: true };
}
